import { Grid } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import BottomBar from 'src/bottom-bar/bottom-bar';
import {
  EmeraldHomePage,
  restoreSalesHomePage,
} from 'src/common/components/emerald-home-page/emerald-home-page';
import HomePageSale from 'src/home-page/components/home-page-sale';
import MenuPanelWrapper from 'src/menu-panel/components/menu-panel-wrapper';
import SidebarWrapper from 'src/sidebar/components/sidebar-wrapper';
import TopAreaWrapper from 'src/top-area/components/top-area-wrapper';
import NTEPCertification from './ntep-certification';
import { styles } from './home-page-styles';
import { connect, useSelector } from 'react-redux';
import { AppState, store } from 'src/common/redux/configure-store';
import { enableScanner, onDataScanned } from 'src/devices/scanner-operations';
import { useEndOfDayStatus } from 'src/end-of-day/hooks/end-of-day-hook';
import { useIdleTimer } from 'react-idle-timer';
import { idleTimeout, lockUser } from 'src/login/business-logic/lock-user';
import { clearEmeraldModals } from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { CustomerDisplayScreen } from 'src/customer-display/enum/customer-display.enum';
import { useIsEmeraldSellingService } from 'src/xlr-configuration/pos-service-type-utils';
import { useInitialHomeState } from '../../hooks/useInitialHomeState';
import { useEODActivity } from '../../hooks/useEODActivity';
import { useShowResetSuccessModal } from '../../hooks/useShowResetSuccessModal';
import { useSidebarButtons } from '../../hooks/useSidebarButtons';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { useResponsiveContext } from 'src/home-page/hooks/responsive-context';

// Scanner operations for cypress test
if (window.Cypress) {
  window.onScan = function(data: string): void {
    enableScanner();
    onDataScanned(data);
  };
}

type Props = ReturnType<typeof mapStateToProps>;
const HomePageLayout: React.FC<Props> = ({
  transaction,
  eodActivity,
  posMode,
  user,
}: Props): JSX.Element => {
  const classes = styles();
  useEndOfDayStatus(transaction.transactionIdExists);
  const { showResetSuccessModal } = useSelector((store: AppState) => store.resetPassword);
  const isEmerald = useIsEmeraldSellingService();
  const sellingMode: string = transaction.transactionIdExists ? 'saleMode' : 'noSaleMode';
  const tenderingMode: boolean = useSelector((store: AppState) => store.tendering.isTendering);
  const activeTransaction: boolean = !!transaction.transactionIdExists;
  const hasCustomer: boolean = !!transaction.transactionCustomer;
  const trainingModeActive: boolean = posMode.trainingMode;
  const { showSidebar } = useResponsiveContext();
  useSidebarButtons(sellingMode, posMode, showSidebar, tenderingMode);
  useShowResetSuccessModal(showResetSuccessModal, user);
  useInitialHomeState(isEmerald);
  useEODActivity(eodActivity);

  const timeout = idleTimeout();
  useIdleTimer(
    timeout
      ? {
          onIdle: async () => {
            if (
              !store.getState().cashDrawer.open &&
              store.getState().customerDisplayScreen.currentScreen !==
                CustomerDisplayScreen.ThankYou
            ) {
              clearEmeraldModals();
              restoreSalesHomePage();
              await withSpinner(async () => {
                await lockUser();
              });
            }
          },
          timeout: timeout,
        }
      : {},
  );
  return (
    <Grid
      container
      direction='column'
      className={clsx('fullHeight', 'fullWidth', classes.container)}
      data-testid='home-page'
      wrap='nowrap'
    >
      {/* Things that are on top of everything else... */}
      <MenuPanelWrapper menuMode='home' />

      <Grid
        item
        className={clsx('fullWidth', classes.topAreaContainer, {
          [classes.topAreaContainerTraining]: trainingModeActive,
        })}
      >
        <TopAreaWrapper />
      </Grid>

      <Grid container className={clsx('fullWidth', 'fullHeight')} wrap='nowrap'>
        {showSidebar && (
          <Grid item className={clsx('fullHeight', classes.sidebarContainer)}>
            <SidebarWrapper isTransactionActive={activeTransaction} hasCustomer={hasCustomer} />
          </Grid>
        )}
        <Grid
          container
          direction='column'
          wrap='nowrap'
          className={clsx('fullHeight', 'fullWidth', classes.mainContainer)}
        >
          <Grid item container direction='column' className={classes.middleAreaContainer}>
            <Grid container item className={classes.middleAreaContainer}>
              <EmeraldHomePage>
                <HomePageSale />
              </EmeraldHomePage>
            </Grid>

            <Grid item container className={clsx('fullWidth', classes.bottomBarContainer)}>
              <BottomBar />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <NTEPCertification />
    </Grid>
  );
};

const mapStateToProps = (state: AppState) => ({
  transaction: state.transaction,
  eodActivity: state.endOfDayActivity,
  posMode: state.posMode,
  user: state.user,
});

export default connect(mapStateToProps)(HomePageLayout);
